import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div>Chargement...</div>;
  const cards = [
    ["Utilisateurs", stats.users],
    ["Clients", stats.customers],
    ["Courtiers", stats.brokers],
    ["Organismes", stats.organizations],
    ["Catégories", stats.categories],
    ["Dossiers", stats.cases],
    ["Payés", stats.paid_cases],
    ["Paiements complets", stats.completed_payments],
    ["Envois postaux", stats.shipments],
    ["Suggestions en attente", stats.suggestions_pending],
  ];
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Administration</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {cards.map(([label, value]) => (
          <div key={label} className="card-elevated p-5" data-testid={`admin-stat-${label.replace(/\s/g, '-')}`}>
            <div className="text-xs uppercase text-muted-foreground mb-2">{label}</div>
            <div className="text-2xl font-bold font-mono">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminUsersPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [roleF, setRoleF] = useState("");
  const [blockedF, setBlockedF] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const load = React.useCallback(async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (roleF) p.set("role", roleF);
    if (blockedF) p.set("blocked", blockedF);
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    const { data } = await api.get(`/admin/users?${p.toString()}`);
    setData(data);
  }, [q, roleF, blockedF, offset]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Utilisateurs <span className="text-lg font-normal text-muted-foreground">({data.total})</span></h1>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input placeholder="Nom, email, téléphone, ID utilisateur, ID dossier, tracking…" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} className="flex-1" data-testid="admin-users-search" />
        <select value={roleF} onChange={(e) => { setRoleF(e.target.value); setOffset(0); }} className="border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="admin-users-role-filter">
          <option value="">Tous rôles</option>
          <option value="customer">Client</option>
          <option value="broker">Courtier</option>
          <option value="admin">Admin</option>
        </select>
        <select value={blockedF} onChange={(e) => { setBlockedF(e.target.value); setOffset(0); }} className="border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="admin-users-blocked-filter">
          <option value="">Actifs & bloqués</option>
          <option value="false">Actifs uniquement</option>
          <option value="true">Bloqués uniquement</option>
        </select>
      </div>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Téléphone</th>
              <th className="text-left px-4 py-3">Rôle</th>
              <th className="text-left px-4 py-3">État</th>
              <th className="text-left px-4 py-3">Vérifié</th>
              <th className="text-left px-4 py-3">Dossiers</th>
              <th className="text-left px-4 py-3">Créé le</th>
              <th className="text-left px-4 py-3">Dernière conn.</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer" onClick={() => nav(`/admin/users/${u.id}`)} data-testid={`admin-user-row-${u.id.slice(0, 8)}`}>
                <td className="px-4 py-3">{u.first_name || "—"} {u.last_name || ""}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.phone || "—"}</td>
                <td className="px-4 py-3"><Badge variant={u.role === "admin" ? "default" : "outline"}>{u.role}</Badge></td>
                <td className="px-4 py-3">{u.blocked ? <Badge variant="destructive">Bloqué</Badge> : <Badge variant="outline" className="text-green-700">Actif</Badge>}</td>
                <td className="px-4 py-3">{u.email_verified ? "✓" : "—"}</td>
                <td className="px-4 py-3 font-mono">{u.cases_count || 0}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm">Voir</Button></td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">Aucun utilisateur.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="text-muted-foreground">{data.items.length} sur {data.total}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Précédent</Button>
          <Button variant="outline" size="sm" disabled={offset + limit >= data.total} onClick={() => setOffset(offset + limit)}>Suivant</Button>
        </div>
      </div>
    </div>
  );
}

export function AdminDirectoryPage() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", website: "", description: "", categories: "" });
  const load = () => api.get(`/admin/organizations${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setOrgs(r.data));
  useEffect(() => { load(); }, [q]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/organizations", {
        name: form.name, website: form.website, description: form.description,
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Organisme ajouté");
      setForm({ name: "", website: "", description: "", categories: "" });
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet organisme ?")) return;
    await api.delete(`/admin/organizations/${id}`); load();
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Annuaire</h1>
        <Button onClick={() => setShowForm(!showForm)} data-testid="admin-new-org-btn">{showForm ? "Fermer" : "Ajouter"}</Button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="card-elevated p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input placeholder="Site web" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          <Input placeholder="Catégories (séparées par ,)" value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} className="md:col-span-2" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2 border border-border rounded-md p-3 bg-background text-sm" rows={3} />
          <Button type="submit" className="md:col-span-2">Créer</Button>
        </form>
      )}
      <Input placeholder="Rechercher..." value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 max-w-sm" />
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr><th className="text-left px-4 py-3">Nom</th><th className="text-left px-4 py-3">Catégories</th><th className="text-left px-4 py-3">Statut</th><th></th></tr></thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{o.name}</td>
                <td className="px-4 py-3 text-xs font-mono">{(o.categories || []).join(", ")}</td>
                <td className="px-4 py-3"><Badge variant="outline">{o.trust_level}</Badge></td>
                <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => del(o.id)}>Supprimer</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminCasesPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const load = React.useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusF) params.set("status", statusF);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    const { data } = await api.get(`/admin/cases?${params.toString()}`);
    setData(data);
  }, [q, statusF, offset]);
  useEffect(() => { load(); }, [load]);

  const clientName = (c) => `${c.client_first_name || ""} ${c.client_last_name || ""}`.trim() || "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Dossiers <span className="text-muted-foreground text-lg font-normal">({data.total})</span></h1>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Rechercher nom, email, téléphone, ID, organisme, tracking…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOffset(0); }}
          className="flex-1 border border-border rounded-md px-3 py-2 text-sm bg-background"
          data-testid="admin-cases-search"
        />
        <select
          value={statusF}
          onChange={(e) => { setStatusF(e.target.value); setOffset(0); }}
          className="border border-border rounded-md px-3 py-2 text-sm bg-background"
          data-testid="admin-cases-status-filter"
        >
          <option value="">Tous les statuts</option>
          <option value="draft">Brouillon</option>
          <option value="pending_human_review">À vérifier</option>
          <option value="approved_for_payment">Approuvé — paiement en attente</option>
          <option value="waiting_payment">En attente de paiement</option>
          <option value="paid">Payé</option>
          <option value="cancelled">Annulé</option>
          <option value="refunded">Remboursé</option>
        </select>
      </div>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Organisme</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-left px-4 py-3">Postal</th>
              <th className="text-left px-4 py-3">Montant</th>
              <th className="text-left px-4 py-3">Créé</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((c) => (
              <tr
                key={c.id}
                className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                onClick={() => nav(`/admin/cases/${c.id}`)}
                data-testid={`admin-case-row-${c.id.slice(0, 8)}`}
              >
                <td className="px-4 py-3 font-mono text-xs">{c.id.slice(0, 8)}</td>
                <td className="px-4 py-3">{clientName(c)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  <div>{c.client_email || "—"}</div>
                  <div>{c.client_phone || ""}</div>
                </td>
                <td className="px-4 py-3">{c.organization_name || "—"}</td>
                <td className="px-4 py-3"><Badge variant="outline">{c.status}</Badge></td>
                <td className="px-4 py-3 text-xs">{c.postal_status || "—"}</td>
                <td className="px-4 py-3 font-mono">{fmtEur(c.price_cents || 0)}</td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" data-testid={`admin-case-view-${c.id.slice(0, 8)}`}>Voir</Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">Aucun dossier ne correspond.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="text-muted-foreground">Page {Math.floor(offset / limit) + 1} — {data.items.length} sur {data.total}</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} data-testid="admin-cases-prev">Précédent</Button>
          <Button variant="outline" size="sm" disabled={offset + limit >= data.total} onClick={() => setOffset(offset + limit)} data-testid="admin-cases-next">Suivant</Button>
        </div>
      </div>
    </div>
  );
}

export function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const load = () => api.get("/admin/payments").then((r) => setPayments(r.data));
  useEffect(() => { load(); }, []);
  const refund = async (id) => {
    if (!window.confirm("Rembourser intégralement ?")) return;
    try {
      await api.post("/admin/payments/refund", { payment_id: id });
      toast.success("Remboursement effectué");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Paiements PayPal</h1>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr><th className="text-left px-4 py-3">ID</th><th className="text-left px-4 py-3">Ordre PayPal</th><th className="text-left px-4 py-3">Montant</th><th className="text-left px-4 py-3">Statut</th><th className="text-left px-4 py-3">Env.</th><th></th></tr></thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="px-4 py-3 font-mono text-xs">{p.id.slice(0, 8)}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.paypal_order_id}</td>
                <td className="px-4 py-3 font-mono">{fmtEur(p.amount_cents)}</td>
                <td className="px-4 py-3"><Badge variant={p.status === "PAYMENT_COMPLETED" ? "default" : "outline"}>{p.status}</Badge></td>
                <td className="px-4 py-3">{p.is_demo ? <Badge variant="secondary">DEMO</Badge> : p.environment}</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "PAYMENT_COMPLETED" && p.amount_refunded_cents === 0 && (
                    <Button variant="ghost" size="sm" onClick={() => refund(p.id)} data-testid={`refund-${p.id}`}>Rembourser</Button>
                  )}
                </td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Aucun paiement.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminShipmentsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/shipments").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Envois postaux</h1>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr><th className="text-left px-4 py-3">Suivi</th><th className="text-left px-4 py-3">Prestataire</th><th className="text-left px-4 py-3">Statut</th><th className="text-left px-4 py-3">Créé</th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-3 font-mono text-xs">{s.tracking_number}</td>
                <td className="px-4 py-3">{s.provider} {s.is_demo && <Badge variant="secondary" className="ml-1">DEMO</Badge>}</td>
                <td className="px-4 py-3"><Badge variant="outline">{s.status}</Badge></td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(s.created_at).toLocaleDateString("fr-FR")}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-muted-foreground">Aucun envoi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminIntegrationsPage() {
  const [i, setI] = useState(null);
  useEffect(() => { api.get("/admin/integrations").then((r) => setI(r.data)); }, []);
  if (!i) return <div>Chargement...</div>;
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Intégrations</h1>
      <div className="card-elevated p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">PayPal Business</h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-muted-foreground">Environnement</dt><dd className="font-mono">{i.paypal.environment}</dd></div>
          <div><dt className="text-muted-foreground">Configuration</dt><dd>{i.paypal.configured ? <Badge>Connecté</Badge> : <Badge variant="destructive">Configuration requise</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Client ID</dt><dd>{i.paypal.client_id_configured ? <Badge>Configuré</Badge> : <Badge variant="outline">Non configuré</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Secret</dt><dd>{i.paypal.client_secret_configured ? <Badge>Configuré</Badge> : <Badge variant="outline">Non configuré</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Webhook</dt><dd>{i.paypal.webhook_configured ? <Badge>Configuré</Badge> : <Badge variant="outline">Non configuré</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Pay Later</dt><dd>{i.paypal.pay_later_enabled ? "Actif" : "Inactif"} ({i.paypal.pay_later_market}) — {i.paypal.pay_later_label}</dd></div>
          <div><dt className="text-muted-foreground">Cartes avancées</dt><dd>{i.paypal.advanced_cards_enabled ? "Actif" : "Configuration PayPal avancée requise"}</dd></div>
          <div><dt className="text-muted-foreground">Webhooks reçus</dt><dd className="font-mono">{i.paypal.webhook_total} (vérifiés : {i.paypal.webhook_verified})</dd></div>
          <div><dt className="text-muted-foreground">Devise</dt><dd className="font-mono">{i.paypal.currency}</dd></div>
        </dl>
        {!i.paypal.configured && (
          <div className="mt-6 p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 text-sm">
            <strong>Configuration requise :</strong> renseignez <code className="font-mono text-xs">PAYPAL_CLIENT_ID</code>, <code className="font-mono text-xs">PAYPAL_CLIENT_SECRET</code> et <code className="font-mono text-xs">PAYPAL_WEBHOOK_ID</code> dans le fichier <code className="font-mono text-xs">backend/.env</code>.
          </div>
        )}
        <div className="mt-4">
          <button
            data-testid="paypal-test-btn"
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/integrations/paypal/test-connection");
                window.alert(`${data.ok ? "✅" : "❌"} ${data.status}`);
              } catch { window.alert("Erreur réseau"); }
            }}
          >Tester la connexion PayPal</button>
        </div>
      </div>
      <div className="card-elevated p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Emails transactionnels — Resend</h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-muted-foreground">Fournisseur</dt><dd className="font-mono">{i.email?.provider || "—"}</dd></div>
          <div><dt className="text-muted-foreground">Clé API</dt><dd>{i.email?.key_configured ? <Badge>Configurée</Badge> : <Badge variant="outline">Non configurée</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Expéditeur configuré</dt><dd>{i.email?.from_email_configured ? <Badge>Configuré</Badge> : <Badge variant="outline">Non configuré</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Reply-to configuré</dt><dd>{i.email?.reply_to_configured ? <Badge>Configuré</Badge> : <Badge variant="outline">Non configuré</Badge>}</dd></div>
          <div><dt className="text-muted-foreground">Domaine</dt><dd className="font-mono text-xs">{i.email?.domain || "—"}</dd></div>
          <div><dt className="text-muted-foreground">Envoi activé</dt><dd>{i.email?.sending_enabled ? <Badge>Actif</Badge> : <Badge variant="destructive">Désactivé</Badge>}</dd></div>
        </dl>
        {!i.email?.sending_enabled && (
          <div className="mt-4 p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 text-sm">
            <strong>Envoi désactivé :</strong> {i.email?.domain_verification_hint || "Le domaine doit être vérifié (DKIM, MX, SPF) sur resend.com/domains avant d'activer l'envoi."}
            Les workflows continuent à fonctionner via les notifications DB en attendant.
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid="resend-test-connection-btn"
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/integrations/resend/test-connection");
                window.alert(`${data.ok ? "✅" : "❌"} ${data.status}${data.domain_status ? ` — domaine ${data.domain_status}` : ""}`);
              } catch { window.alert("Erreur réseau"); }
            }}
          >Tester la connexion</button>
          <button
            data-testid="resend-send-test-btn"
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/integrations/resend/send-test-email", {});
                window.alert(`${data.ok ? "✅" : "❌"} ${data.status}${data.provider_id ? ` — id ${data.provider_id}` : ""}${data.reason && !data.ok ? ` — ${data.reason}` : ""}`);
              } catch { window.alert("Erreur réseau"); }
            }}
          >Envoyer un email test</button>
        </div>
      </div>
      <div className="card-elevated p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">La Poste — ControlAdresse V2</h2>
        {(() => {
          const ca = i.laposte?.control_adresse || {};
          const quotaAlert = ca.alerts || {};
          return (
            <>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-muted-foreground">État</dt><dd>{ca.configured ? <Badge>Configurée</Badge> : <Badge variant="destructive">Non configurée</Badge>}</dd></div>
                <div><dt className="text-muted-foreground">Base URL</dt><dd className="font-mono text-xs break-all">{ca.base_url}</dd></div>
                <div><dt className="text-muted-foreground">Auth</dt><dd className="font-mono text-xs">{ca.auth_scheme}</dd></div>
                <div><dt className="text-muted-foreground">Clé Okapi</dt><dd>{ca.configured ? <Badge>Clé configurée</Badge> : <Badge variant="outline">Clé non configurée</Badge>}</dd></div>
                <div><dt className="text-muted-foreground">Quota annuel</dt><dd className="font-mono">{ca.yearly_quota}</dd></div>
                <div><dt className="text-muted-foreground">Consommation année</dt><dd className="font-mono">{ca.consumption?.year} / {ca.yearly_quota} ({ca.consumption?.percent_used}%)</dd></div>
                <div><dt className="text-muted-foreground">Mois en cours</dt><dd className="font-mono">{ca.consumption?.month}</dd></div>
                <div><dt className="text-muted-foreground">Jour</dt><dd className="font-mono">{ca.consumption?.day}</dd></div>
                <div><dt className="text-muted-foreground">Restant</dt><dd className="font-mono">{ca.consumption?.remaining}</dd></div>
                <div><dt className="text-muted-foreground">Adresses en cache</dt><dd className="font-mono">{ca.cache_size}</dd></div>
                <div><dt className="text-muted-foreground">Appels live / cache</dt><dd className="font-mono">{ca.live_calls_total ?? 0} / {ca.cache_hits_total ?? 0} ({ca.cache_ratio_percent ?? 0}%)</dd></div>
                <div><dt className="text-muted-foreground">Dernier succès</dt><dd className="font-mono text-xs">{ca.last_success_at ? new Date(ca.last_success_at).toLocaleString("fr-FR") : "—"}</dd></div>
                <div><dt className="text-muted-foreground">Erreurs cumulées</dt><dd className="font-mono">{ca.errors?.total || 0}</dd></div>
              </dl>
              {(quotaAlert.warn_95 || quotaAlert.warn_85 || quotaAlert.warn_70) && (
                <div className="mt-4 p-3 rounded-md text-sm border" style={{ background: quotaAlert.warn_95 ? "#fef2f2" : quotaAlert.warn_85 ? "#fff7ed" : "#fefce8", borderColor: "#fde68a" }} data-testid="laposte-quota-alert">
                  ⚠ Quota ControlAdresse à <strong>{ca.consumption?.percent_used}%</strong> du plan annuel — surveillez la consommation.
                </div>
              )}
              {!ca.configured && (
                <div className="mt-4 p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 text-sm">
                  <strong>Configuration requise :</strong> renseignez <code className="font-mono text-xs">LAPOSTE_OKAPI_KEY</code> dans <code className="font-mono text-xs">backend/.env</code> (clé issue de votre projet Okapi <code>{process.env.REACT_APP_LAPOSTE_PROJECT || "developer.laposte.fr"}</code>), puis <code>sudo supervisorctl restart backend</code>. Le formulaire d'adresse fonctionne en mode manuel tant que la clé est absente.
                </div>
              )}
              <div className="mt-4">
                <button
                  data-testid="laposte-test-btn"
                  className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/admin/integrations/laposte/test-control-address");
                      window.alert(data.ok ? `Test OK — ${data.suggestion_count} suggestions (ex : ${data.sample_first})` : `Test échoué : ${data.reason}`);
                    } catch (e) { window.alert("Erreur réseau"); }
                  }}
                >Test connexion (1 appel du quota)</button>
              </div>
            </>
          );
        })()}
      </div>
      <div className="card-elevated p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">La Poste — Digiposte V3</h2>
        {(() => {
          const dp = i.laposte?.digiposte || {};
          return (
            <>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-muted-foreground">État</dt><dd>{dp.configured ? <Badge>Configurée</Badge> : <Badge variant="destructive">Non configurée</Badge>}</dd></div>
                <div><dt className="text-muted-foreground">Base URL</dt><dd className="font-mono text-xs break-all">{dp.base_url}</dd></div>
                <div><dt className="text-muted-foreground">Auth</dt><dd className="font-mono text-xs">{dp.auth_scheme}</dd></div>
                <div><dt className="text-muted-foreground">Client ID OAuth2</dt><dd>{dp.client_id_present ? "présent" : "absent"}</dd></div>
                <div><dt className="text-muted-foreground">Client secret</dt><dd>{dp.client_secret_present ? "présent" : "absent"}</dd></div>
                <div><dt className="text-muted-foreground">Redirect URI</dt><dd>{dp.redirect_uri_configured ? "configurée" : "absente"}</dd></div>
                <div><dt className="text-muted-foreground">Transmissions</dt><dd className="font-mono">{dp.transmissions_total}</dd></div>
              </dl>
              {!dp.configured && dp.blocker && (
                <div className="mt-4 p-4 rounded-md bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 text-sm">
                  <strong>DIGIPOSTE_UNCONFIGURED :</strong> {dp.blocker}
                  <div className="mt-2 text-xs">Aucun document n'est envoyé tant que l'OAuth2 n'est pas complet. Endpoints attendus : <code className="font-mono">https://auth.digiposte.fr/authorize</code> · <code className="font-mono">https://auth.digiposte.fr/token</code>.</div>
                </div>
              )}
              <div className="mt-3 text-xs text-muted-foreground">Types de documents autorisés : {(dp.supported_document_types_allowlist || []).join(", ")}.</div>
            </>
          );
        })()}
      </div>
      <div className="card-elevated p-6">
        <h2 className="text-lg font-semibold mb-4">Prestataire postal — MySendingBox</h2>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-muted-foreground">Prestataire postal</dt><dd className="font-mono">MySendingBox</dd></div>
          <div><dt className="text-muted-foreground">Environnement</dt><dd><Badge>Live</Badge></dd></div>
          <div><dt className="text-muted-foreground">Produit</dt><dd className="font-mono">LRAR (recommandé AR)</dd></div>
          <div><dt className="text-muted-foreground">Connexion</dt><dd><Badge>OK</Badge></dd></div>
          <div><dt className="text-muted-foreground">Webhook</dt><dd><Badge>Configuré</Badge></dd></div>
          <div><dt className="text-muted-foreground">Validation admin obligatoire</dt><dd><Badge>Active</Badge></dd></div>
          <div><dt className="text-muted-foreground">Envoi auto après paiement</dt><dd><Badge variant="secondary">Désactivé</Badge></dd></div>
        </dl>
        <div className="mt-4 text-xs text-muted-foreground">
          Pour le pilotage détaillé (test connexion, dossiers éligibles, déclenchement manuel LRAR), rendez-vous dans le module <strong>MySendingBox (Live)</strong> du menu.
        </div>
      </div>
    </div>
  );
}

export function AdminAuditPage() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight mb-6">Journal d'audit</h1>
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Rôle</th><th className="text-left px-4 py-3">Action</th><th className="text-left px-4 py-3">Entité</th><th className="text-left px-4 py-3">ID</th></tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-4 py-3 font-mono text-xs">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                <td className="px-4 py-3">{l.actor_role}</td>
                <td className="px-4 py-3">{l.action}</td>
                <td className="px-4 py-3">{l.entity}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.entity_id?.slice(0, 8) || "—"}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Aucun événement.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
